import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import BrandScroller from '../components/BrandScroller';
import TextReveal from '../components/TextReveal';
import ParallaxSection from '../components/ParallaxSection';
import { AnimatedDivider, AnimatedStar } from '../components/SVGAnimation';
import { HeroSkeleton } from '../components/SkeletonLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/projects/featured`),
          axios.get(`${API}/services`)
        ]);
        const validProjects = projectsRes.data.filter(p =>
          p.video_urls && p.video_urls.length > 0 && p.image_url
        );
        setFeaturedProjects(validProjects.slice(0, 4));
        setServices(servicesRes.data);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  if (loading) return <HeroSkeleton />;

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section
        className="min-h-screen flex flex-col items-center justify-center px-6 py-20"
        data-testid="hero-section"
        data-cursor-color="var(--cursor-accent)"
      >
        <div className="max-w-6xl mx-auto text-center">
          {/* Golden Ratio Typography: H1 ÷ φ² = H2, H2 ÷ φ = tagline */}
          <div className="space-y-10">
            <TextReveal
              as="h1"
              className="font-black leading-none"
              style={{ fontSize: 'clamp(3.2rem, 8vw, 8rem)', lineHeight: '0.93', fontWeight: '900', letterSpacing: '-0.04em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              If It Doesn't Move The Business, It Isn't Creative.
            </TextReveal>

            <motion.div
              className="space-y-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.23, 1, 0.32, 1] }}
            >
              <h2
                style={{ fontSize: 'clamp(1.4rem, 3vw, 3rem)', lineHeight: '1.1', fontWeight: '400', letterSpacing: '-0.01em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                Independent Creative Director
              </h2>
              <p
                className="uppercase tracking-[0.2em]"
                style={{ fontSize: 'clamp(0.7rem, 0.9vw, 0.9rem)', fontWeight: '300', color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}
              >
                Strategy-Led Creative for Ambitious Brands
              </p>
            </motion.div>
          </div>

          <motion.div
            className="pt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            <motion.svg
              className="w-8 h-8 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: 'var(--text-tertiary)' }}
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </motion.svg>
          </motion.div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-20 px-6" data-testid="featured-work">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="featured-grid">
              {featuredProjects.map((project, index) => {
                const videoId = project.video_urls?.[0] ? getYouTubeVideoId(project.video_urls[0]) : null;
                const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : project.image_url;

                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.6, delay: index * 0.1, ease: [0.23, 1, 0.32, 1] }}
                  >
                    <Link
                      to={`/work/${project.id}`}
                      className="project-tile block"
                      data-testid={`project-tile-${project.id}`}
                      data-cursor-hover
                    >
                      <img src={project.image_url || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '')} alt={project.title} loading="lazy" />
                      {videoId && (
                        <div className="video-preview">
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&modestbranding=1&rel=0`}
                            title={project.title}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        </div>
                      )}
                      <div className="project-tile-overlay">
                        <p className="project-tile-client">{project.client}</p>
                        <h3 className="project-tile-title">{project.title}</h3>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </FadeInSection>

          <FadeInSection className="text-center mt-16" delay={0.2}>
            <Link
              to="/work"
              className="text-base underline hover:opacity-60 transition-opacity"
              style={{ color: 'var(--text-primary)' }}
              data-cursor-hover
            >
              View All Work
            </Link>
          </FadeInSection>
        </div>
      </section>

      <AnimatedDivider className="max-w-7xl mx-auto px-6" />

      {/* Trust Logos */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }} data-testid="trust-logos">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <h2 className="text-center text-sm uppercase tracking-widest mb-12" style={{ color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}>
              <span className="accent-amethyst">50+</span> Brands Touched
            </h2>
          </FadeInSection>
          <BrandScroller />
        </div>
      </section>

      {/* Hire Me For */}
      <section className="py-32 px-6" data-testid="hire-me-section">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <h2
              className="font-black mb-16 text-center"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)', lineHeight: '1.0', fontWeight: '900', letterSpacing: '-0.03em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              <span className="accent-yellow">Hire</span> Me For
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-12 text-center max-w-4xl mx-auto">
            {services.slice(0, 5).map((service, index) => (
              <FadeInSection key={service.id} delay={index * 0.1}>
                <div className="group">
                  <AnimatedStar className="mx-auto mb-4 opacity-30" size={24} />
                  <h3
                    className="text-xl md:text-2xl font-bold transition-all duration-300 group-hover:scale-110"
                    style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
                    data-cursor-hover
                  >
                    {service.title}
                  </h3>
                </div>
              </FadeInSection>
            ))}
            <FadeInSection delay={0.5}>
              <div className="group flex flex-col items-center">
                <AnimatedStar className="mx-auto mb-4 opacity-30" size={24} />
                <p
                  className="text-xl md:text-2xl font-bold italic transition-transform duration-300 hover:scale-110"
                  style={{ color: 'var(--text-tertiary)', fontFamily: 'Nohemi, sans-serif' }}
                >
                  and more
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }} data-testid="cta-section">
        <div className="max-w-5xl mx-auto text-center space-y-12">
          <ParallaxSection speed={0.2}>
            <TextReveal
              as="h2"
              className="font-black"
              style={{ fontSize: 'clamp(3.5rem, 8vw, 8rem)', lineHeight: '0.95', fontWeight: '900', letterSpacing: '-0.04em', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            >
              Have a Brand Problem?
            </TextReveal>
          </ParallaxSection>

          <FadeInSection delay={0.2}>
            <p className="text-2xl font-light" style={{ color: 'var(--text-secondary)', fontFamily: 'Nohemi, sans-serif' }}>
              Let's Solve It.
            </p>
          </FadeInSection>

          <FadeInSection delay={0.4}>
            <Link to="/contact" data-cursor-hover>
              <button
                className="px-8 py-3 text-base font-medium rounded-full hover:opacity-80 hover:scale-105 transition-all"
                style={{
                  backgroundColor: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                  fontFamily: 'Nohemi, sans-serif',
                }}
                data-testid="cta-button"
              >
                Start a Conversation
              </button>
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
